/**
 * Session-scoped log of the document edits AIVA has applied in this task-pane
 * session. Each mutating Word handler records an entry when its edit actually
 * lands in the document; the /changes slash command appends the formatted log
 * to the document context so the Track Changes skill can report exactly what
 * changed and when. Module-level (not persisted) — the log resets when the
 * task pane reloads, matching the per-session scope of the chat itself.
 */


export type ChangeVerifier =
  | { kind: "textPresent"; text: string }
  | { kind: "toc" }
  | { kind: "pageNumbers"; position: "header" | "footer" }
  | { kind: "bates"; prefix: string; position: "header" | "footer" }
  | { kind: "tracking"; enabled: boolean }
  | { kind: "comment"; id: string; resolved?: boolean };

export type VerifyFn = (verifier: ChangeVerifier) => Promise<boolean>;

export interface WordChangeEntry {
  /** Human-readable action, e.g. "Replaced selection". */
  action: string;
  /** What changed — quoted snippets, counts, positions. */
  detail: string;
  /** Wall-clock ms when the change was applied. */
  timestamp: number;

  verify?: ChangeVerifier;
}

/** Marker heading the /changes skill prompt looks for in the document context. */
export const WORD_CHANGE_LOG_MARKER = "--- AIVA Session Changes ---";

export const WORD_COMPLETED_ACTIONS_MARKER = "--- AIVA Completed This Session (verified against the current document) ---";

export const WORD_UNDONE_ACTIONS_MARKER = "--- AIVA Verified NOT In The Document (undone or removed since) ---";

const MAX_SNIPPET_CHARS = 160;

const entries: WordChangeEntry[] = [];

// Captured when the task pane loads — the boundary of "this session". Used to
// scope the /changes report (native tracked revisions + comments) to edits
// made since the pane opened, instead of the document's full revision history.
const sessionStartMs = Date.now();

export function getSessionStartMs(): number {
  return sessionStartMs;
}

/** Collapse whitespace and cap length so log lines stay one-line quotable. */
export function changeSnippet(text: string): string {
  const collapsed = text.replace(/\s+/g, " ").trim();
  return collapsed.length <= MAX_SNIPPET_CHARS
    ? collapsed
    : `${collapsed.slice(0, MAX_SNIPPET_CHARS)}…`;
}


const MAX_KEY_CHARS = 120;

/**
 * `replaced` is the text the edit overwrote. When given, the key is taken from
 * where the new text diverges from it: an /improve rewrite usually keeps the
 * opening words, so a key from the first line would still match the original
 * after a Ctrl+Z and the undo would go unnoticed.
 */
export function textPresenceVerifier(text: string, replaced = ""): ChangeVerifier | undefined {
  const lines = text.split(/\r?\n/).map((l) => l.replace(/\s+/g, " ").trim()).filter(Boolean);
  const firstLine = lines[0];
  if (!firstLine) return undefined;
  const old = replaced.replace(/\s+/g, " ").trim().toLowerCase();
  if (old) {
    for (const line of lines) {
      const key = keyAbsentFrom(line, old);
      if (key) return { kind: "textPresent", text: key };
    }
  }
  return { kind: "textPresent", text: firstLine.slice(0, MAX_KEY_CHARS) };
}

/** A window of `line` (case-insensitively) absent from `old`, anchored just
 *  before the point where `line` stops matching anything in `old`. */
function keyAbsentFrom(line: string, old: string): string | undefined {
  const lower = line.toLowerCase();
  if (old.includes(lower)) return undefined;
  let p = 0;
  while (p < lower.length && old.includes(lower.slice(0, p + 1))) p++;
  for (let s = Math.max(0, p - MAX_KEY_CHARS / 2); s < line.length; s++) {
    const key = line.slice(s, s + MAX_KEY_CHARS);
    if (!old.includes(key.toLowerCase())) return key;
  }
  return undefined;
}

export function recordWordChange(action: string, detail: string, verify?: ChangeVerifier): void {
  entries.push({ action, detail, timestamp: Date.now(), ...(verify ? { verify } : {}) });
}

export function getWordChangeLog(): readonly WordChangeEntry[] {
  return entries;
}

export function clearWordChangeLog(): void {
  entries.length = 0;
}


export interface VerifiedChangeLog {
  /** Entries whose effect is still in the document, plus any that couldn't be
   *  verified (no verifier, or an indeterminate read) — those are trusted. */
  present: WordChangeEntry[];
  /** Entries whose verifier confirmed the effect is GONE — the user undid or
   *  removed it. Surfaced as an explicit negative so the model is corrected. */
  undone: WordChangeEntry[];
}


export async function verifyWordChangeLog(verify: VerifyFn): Promise<VerifiedChangeLog> {
  const outcomes = await Promise.all(
    entries.map(async (entry): Promise<"present" | "undone"> => {
      if (!entry.verify) return "present";
      try {
        return (await verify(entry.verify)) ? "present" : "undone";
      } catch {
        return "present";
      }
    }),
  );
  return {
    present: entries.filter((_, i) => outcomes[i] === "present"),
    undone: entries.filter((_, i) => outcomes[i] === "undone"),
  };
}

export async function getVerifiedWordChangeLog(verify: VerifyFn): Promise<WordChangeEntry[]> {
  return (await verifyWordChangeLog(verify)).present;
}

/**
 * True for entries that changed the DOCUMENT, false for the `/start-tracking`
 * and `/stop-tracking` toggles (AIVA-8040).
 *
 * A toggle is session state, not an edit, and reporting it was actively wrong in
 * two ways that compounded:
 *
 *  1. The `tracking` verifier keeps a toggle entry only while the live mode
 *     still matches what it recorded (see changeVerify). On a report turn the
 *     live mode is, by construction, whatever the OTHER command last set — so
 *     "Started tracking" survived exactly when "Stopped tracking" did not, and
 *     vice versa.
 *  2. The log block is assembled before the turn's `word_set_tracking` runs, so
 *     the current toggle is never in it.
 *
 * Together those produced the reported alternation: the first run showed the
 * stop timestamps, the next run showed the start ones. Neither was what the user
 * asked for — they wanted the document changes. The live tracking state is
 * reported separately from the tool result, so nothing is lost by dropping these.
 */
export function isDocumentEditEntry(entry: WordChangeEntry): boolean {
  return entry.verify?.kind !== "tracking";
}

/** Format a numbered, timestamped list of entries under the /changes marker, or
 *  "" for an empty list. Pure over its argument so the same shape serves the
 *  full log and a verified subset. */
export function formatChangeLogEntries(list: readonly WordChangeEntry[]): string {
  if (list.length === 0) return "";
  const lines = list.map((entry, i) => {
    const time = new Date(entry.timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${i + 1}. [${time}] ${entry.action}: ${entry.detail}`;
  });
  return `\n\n${WORD_CHANGE_LOG_MARKER}\n${lines.join("\n")}`;
}


export function formatCompletedActionsBlock(list: readonly WordChangeEntry[]): string {
  if (list.length === 0) return "";
  const lines = list.map((entry) => `- ${entry.action}: ${entry.detail}`);
  return (
    `\n\n${WORD_COMPLETED_ACTIONS_MARKER}\n` +
    `AIVA has already applied the following in this document — do not offer to do them again ` +
    `unless the user asks:\n${lines.join("\n")}`
  );
}


export function formatUndoneActionsBlock(list: readonly WordChangeEntry[]): string {
  if (list.length === 0) return "";
  const lines = list.map((entry) => `- ${entry.action}: ${entry.detail}`);
  return (
    `\n\n${WORD_UNDONE_ACTIONS_MARKER}\n` +
    `AIVA applied these earlier this session, but they are NO LONGER in the document ` +
    `(the user undid or removed them). Treat them as NOT done: if asked whether they are ` +
    `present, say they are not, and offer to apply them again if appropriate. This current ` +
    `document state overrides any earlier message where AIVA said it applied them:\n${lines.join("\n")}`
  );
}
